import { ArrowRight, Check, Gauge, Plug } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { Section, SectionHeader } from "@/components/marketing/Section";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { appUrl, env } from "@/lib/env";
import { preserveSearch } from "@/lib/nav";

type BillingInterval = "week" | "year";
type PlanId = "free" | "pro" | "max";

type Plan = {
  id: PlanId;
  weeklyAmountCents: number;
  yearlyAmountCents: number;
  includedAiCredits: number;
  featured?: boolean;
  /** Per-plan rows carry only what differs between tiers. */
  comingSoonFeatures?: boolean;
};

/**
 * Display mirror of the app's billing catalog — amounts from
 * apps/server/src/accounts/billing/plans.ts, allowances from
 * apps/server/src/accounts/billing/usage-periods.ts (`TIER_INCLUDED_AI_CREDITS`),
 * and the card layout from apps/client/src/features/settings/ui/BillingSection.tsx.
 * Change them together: this page must never advertise a price the checkout
 * does not charge.
 */
const PLANS: readonly Plan[] = [
  { id: "free", weeklyAmountCents: 0, yearlyAmountCents: 0, includedAiCredits: 50 },
  {
    id: "pro",
    weeklyAmountCents: 999,
    yearlyAmountCents: 31_148,
    includedAiCredits: 1_000,
    comingSoonFeatures: true,
  },
  {
    id: "max",
    weeklyAmountCents: 4_999,
    yearlyAmountCents: 129_948,
    includedAiCredits: 10_000,
    featured: true,
    comingSoonFeatures: true,
  },
];

/** Where a paid CTA lands inside the app once signed in. */
const PLANS_APP_PATH = "/settings?section=billing";

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usdWhole = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const number = new Intl.NumberFormat("en-US");

function effectiveWeeklyCents(plan: Plan, interval: BillingInterval): number {
  return interval === "year" ? plan.yearlyAmountCents / 52 : plan.weeklyAmountCents;
}

/** Same derivation as the app: never a hand-kept discount constant. */
function yearlyDiscountPct(plan: Plan): number {
  if (plan.weeklyAmountCents === 0) return 0;
  return Math.round((1 - plan.yearlyAmountCents / 52 / plan.weeklyAmountCents) * 100);
}

const MAX_YEARLY_DISCOUNT_PCT = Math.max(...PLANS.map(yearlyDiscountPct));

const INCLUDED_ROWS: readonly { key: string; detailKey?: string }[] = [
  { key: "alerts" },
  { key: "scheduled" },
  { key: "marketData", detailKey: "marketDataDetail" },
  { key: "social", detailKey: "socialDetail" },
  { key: "skills", detailKey: "skillsDetail" },
  { key: "models" },
];

function IntervalToggle({
  interval,
  onChange,
}: {
  interval: BillingInterval;
  onChange: (next: BillingInterval) => void;
}) {
  const { t } = useTranslation();
  const options: BillingInterval[] = ["week", "year"];
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        role="tablist"
        aria-label={t("pricing.billing.label")}
        className="inline-flex rounded-full border border-border bg-card p-1"
      >
        {options.map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={interval === option}
            onClick={() => onChange(option)}
            className={cn(
              "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
              interval === option
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t(option === "week" ? "pricing.billing.weekly" : "pricing.billing.yearly")}
          </button>
        ))}
      </div>
      <p className="font-mono text-eyebrow uppercase text-primary">
        {t("pricing.billing.yearlySavings", { percent: MAX_YEARLY_DISCOUNT_PCT })}
      </p>
    </div>
  );
}

function PlanPrice({ plan, interval }: { plan: Plan; interval: BillingInterval }) {
  const { t } = useTranslation();
  if (plan.weeklyAmountCents === 0) {
    return (
      <p className="font-mono text-3xl font-medium tabular-nums text-foreground">
        {t("pricing.plans.free.price")}
      </p>
    );
  }
  const weekly = usd.format(effectiveWeeklyCents(plan, interval) / 100);
  return (
    <div>
      <p className="flex items-baseline gap-1.5">
        <span className="font-mono text-3xl font-medium tabular-nums text-foreground">
          {weekly}
        </span>
        <span className="text-sm text-muted-foreground">{t("pricing.billing.perWeek")}</span>
        {interval === "year" ? (
          <span className="ml-1 rounded-full bg-primary/15 px-2 py-0.5 font-mono text-[10px] font-medium uppercase text-primary">
            {t("pricing.billing.percentOff", { percent: yearlyDiscountPct(plan) })}
          </span>
        ) : null}
      </p>
      <p className="mt-1 min-h-4 text-xs text-muted-foreground">
        {interval === "year"
          ? t("pricing.billing.billedAnnually", {
              amount: usdWhole.format(plan.yearlyAmountCents / 100),
            })
          : null}
      </p>
    </div>
  );
}

function PlanCard({
  plan,
  interval,
  ctaHref,
}: {
  plan: Plan;
  interval: BillingInterval;
  ctaHref: string;
}) {
  const { t } = useTranslation();
  const features = t(`pricing.plans.${plan.id}.features`, { returnObjects: true }) as string[];
  const comingSoon = plan.comingSoonFeatures ? ` (${t("pricing.comingSoon")})` : "";
  const isPaid = plan.weeklyAmountCents > 0;

  return (
    <article
      className={cn(
        "glass-card flex h-full flex-col gap-6 rounded-[2rem] p-7",
        plan.featured && "border-primary/35 shadow-glow-accent",
      )}
    >
      <div>
        <div className="flex items-center justify-between">
          <p
            className={cn(
              "font-mono text-eyebrow uppercase",
              plan.featured ? "text-primary" : "text-subtle-foreground",
            )}
          >
            {t(`pricing.plans.${plan.id}.name`)}
          </p>
          {plan.featured ? (
            <span className="rounded-full bg-primary/15 px-2 py-0.5 font-mono text-[10px] font-medium uppercase text-primary">
              {t("pricing.bestValue")}
            </span>
          ) : null}
        </div>
        <div className="mt-3">
          <PlanPrice plan={plan} interval={interval} />
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {t(`pricing.plans.${plan.id}.tagline`)}
        </p>
      </div>

      <div
        className="flex items-center gap-2 rounded-card border border-border bg-card/60 px-3 py-2"
        title={t("pricing.creditsTooltip")}
      >
        <Gauge className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        <p className="text-sm text-foreground">
          <span className="font-mono text-base font-semibold tabular-nums">
            {number.format(plan.includedAiCredits)}
          </span>{" "}
          {t("pricing.creditsWeekly")}
        </p>
      </div>

      <ul className="flex flex-1 flex-col gap-2">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span>
              {feature}
              {comingSoon}
            </span>
          </li>
        ))}
      </ul>

      <Button
        variant={plan.featured ? "primary" : "secondary"}
        className="w-full"
        href={ctaHref}
        trailing={isPaid ? undefined : <ArrowRight className="h-4 w-4" aria-hidden />}
      >
        {t(`pricing.plans.${plan.id}.cta`)}
      </Button>
    </article>
  );
}

function IncludedInEveryPlan() {
  const { t } = useTranslation();
  return (
    <div className="mt-6 rounded-[2rem] border border-border bg-card/40 p-7">
      <p className="text-sm font-medium text-muted-foreground">{t("pricing.includedTitle")}</p>
      <div className="mt-4 flex items-start gap-3 rounded-card bg-card px-4 py-3">
        <Plug className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <div>
          <p className="text-sm font-medium text-foreground">{t("pricing.included.mcp")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("pricing.included.mcpDetail")}</p>
        </div>
      </div>
      <ul className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
        {INCLUDED_ROWS.map((row) => (
          <li key={row.key} className="flex items-start gap-2 text-sm text-muted-foreground">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span>
              <span className="text-foreground">{t(`pricing.included.${row.key}`)}</span>
              {row.detailKey ? (
                <span className="mt-0.5 block text-xs text-subtle-foreground">
                  {t(`pricing.included.${row.detailKey}`)}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type FaqItem = { id: string; question: string; answer: string };

export function Pricing() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const referralCode = new URLSearchParams(search).get("ref");
  const [interval, setInterval] = useState<BillingInterval>("week");
  const faq = t("pricing.faq", { returnObjects: true }) as FaqItem[];

  // Before launch every CTA joins the waitlist; after launch free lands on
  // sign-in and paid plans land on Plan & billing behind sign-in.
  const ctaHref = (plan: Plan): string => {
    if (!env.launched) return preserveSearch("/#waitlist", search);
    if (plan.weeklyAmountCents === 0) return appUrl("/sign-in", referralCode);
    const url = new URL(appUrl("/sign-in", referralCode));
    url.searchParams.set("next", PLANS_APP_PATH);
    return url.toString();
  };

  return (
    <>
      <Section id="pricing" className="pt-12">
        <SectionHeader
          pageTitle
          align="center"
          title={t("pricing.title")}
          subtitle={t("pricing.subtitle")}
        />
        <div className="mt-10">
          <IntervalToggle interval={interval} onChange={setInterval} />
        </div>
        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} interval={interval} ctaHref={ctaHref(plan)} />
          ))}
        </div>
        <IncludedInEveryPlan />
        <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-subtle-foreground">
          {t("pricing.note")}
        </p>
      </Section>

      <Section>
        <SectionHeader title={t("pricing.faqTitle")} />
        <dl className="mt-8 grid gap-6 md:grid-cols-2">
          {faq.map((item) => (
            <div key={item.id} className="rounded-card border border-border bg-card p-6">
              <dt className="text-base font-semibold text-foreground">{item.question}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.answer}</dd>
            </div>
          ))}
        </dl>
      </Section>
    </>
  );
}
